import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  useAppointment,
  useExtras,
  useLengths,
  useServices,
} from '@/lib/queries';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { fmtMoney, todayISO } from '@/lib/format';
import { ClientPicker } from '@/components/ClientPicker';
import { hapticNotify } from '@/lib/telegram';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/components/PageHeader';

interface Props {
  mode: 'create' | 'edit' | 'reschedule';
}

export const AppointmentFormPage = ({ mode }: Props) => {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { data: existing } = useAppointment(mode !== 'create' ? id : undefined);
  const { data: services = [] } = useServices();
  const { data: extras = [] } = useExtras();
  const { data: lengths = [] } = useLengths();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [client_id, setClientId] = useState<string>(searchParams.get('client') ?? '');
  const [service_id, setServiceId] = useState<string>(searchParams.get('service') ?? '');
  const [date, setDate] = useState<string>(searchParams.get('date') ?? todayISO());
  const [start_time, setStartTime] = useState<string>('10:00');
  const [duration, setDuration] = useState<number | ''>(240);
  const [strands, setStrands] = useState<number | ''>('');
  const [length_cm, setLengthCm] = useState<number | ''>('');
  const [hair_grams, setHairGrams] = useState<number | ''>('');
  const [hair_added, setHairAdded] = useState(false);
  const [hair_price, setHairPrice] = useState<number | ''>('');
  const [tangle_removal, setTangleRemoval] = useState(false);
  const [tangle_price, setTanglePrice] = useState<number | ''>('');
  const [work_price, setWorkPrice] = useState<number | ''>('');
  const [note, setNote] = useState('');
  const [extras_picked, setExtrasPicked] = useState<{ extra_service_id: string; price: number }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!existing) return;
    setClientId(existing.client_id);
    setServiceId(existing.service_id);
    if (mode !== 'reschedule') {
      setDate(existing.date.slice(0, 10));
      setStartTime(existing.start_time);
    }
    setDuration(existing.planned_duration_min);
    setStrands(existing.strands_count ?? '');
    setLengthCm(existing.hair_length_cm ?? '');
    setHairGrams(existing.hair_grams ? Number(existing.hair_grams) : '');
    setHairAdded(existing.hair_added);
    setHairPrice(existing.hair_price ? Number(existing.hair_price) : '');
    setTangleRemoval(!!existing.tangle_removal);
    setTanglePrice(existing.tangle_price ? Number(existing.tangle_price) : '');
    setWorkPrice(existing.work_price ? Number(existing.work_price) : '');
    setNote(existing.note ?? '');
    setExtrasPicked(
      (existing.extras ?? []).map((e: any) => ({
        extra_service_id: e.extra_service_id,
        price: Number(e.price),
      })),
    );
  }, [existing, mode]);

  const service = useMemo(
    () => services.find((s: any) => s.id === service_id),
    [services, service_id],
  );

  // When service is picked, prefill defaults
  useEffect(() => {
    if (service && !existing) {
      setDuration(service.default_duration_min);
      if (work_price === '') setWorkPrice(Number(service.default_price));
    }
  }, [service]); // eslint-disable-line

  const { master } = useAuthStore();
  const isOutOfHours = useMemo(() => {
    const wh = (master as any)?.working_hours as Record<string, { from: string | null; to: string | null }> | undefined;
    if (!wh) return false;
    const day = new Date(date + 'T00:00:00');
    const keys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
    const w = wh[keys[day.getDay()]!];
    if (!w || !w.from || !w.to) return true;
    const parseHHMM = (t: string) => {
      const [h, m] = t.split(':').map(Number);
      return (h ?? 0) * 60 + (m ?? 0);
    };
    const startMin = parseHHMM(start_time);
    const endMin = startMin + Number(duration || 0);
    return startMin < parseHHMM(w.from) || endMin > parseHHMM(w.to);
  }, [master, date, start_time, duration]);

  const total = useMemo(() => {
    const w = Number(work_price || 0);
    const h = hair_added ? Number(hair_price || 0) : 0;
    const t = tangle_removal ? Number(tangle_price || 0) : 0;
    const ex = extras_picked.reduce((s, e) => s + Number(e.price || 0), 0);
    return w + h + t + ex;
  }, [work_price, hair_price, hair_added, tangle_price, tangle_removal, extras_picked]);

  const toggleExtra = (extra_id: string, default_price: number) => {
    setExtrasPicked((prev) => {
      const ex = prev.find((p) => p.extra_service_id === extra_id);
      if (ex) return prev.filter((p) => p.extra_service_id !== extra_id);
      return [...prev, { extra_service_id: extra_id, price: default_price }];
    });
  };

  const submit = async () => {
    if (submitting) return;
    setError(null);
    if (!client_id) return setError('Выберите клиента');
    if (!service_id) return setError('Выберите услугу');

    setSubmitting(true);
    try {
      const planned_duration_min = Math.max(15, Number(duration) || 15);
      const body = {
        client_id,
        service_id,
        date,
        start_time,
        planned_duration_min,
        strands_count: strands === '' ? null : Number(strands),
        hair_length_cm: length_cm === '' ? null : Number(length_cm),
        hair_grams: hair_grams === '' ? null : Number(hair_grams),
        hair_added,
        hair_price: hair_added ? Number(hair_price || 0) : 0,
        tangle_removal,
        tangle_price: tangle_removal ? Number(tangle_price || 0) : 0,
        work_price: Number(work_price || 0),
        note,
        extras: extras_picked,
      };

      if (mode === 'create') {
        const { data } = await api.post('/appointments', body);
        hapticNotify('success');
        nav(`/appointments/${data.id}`);
      } else if (mode === 'edit') {
        await api.patch(`/appointments/${id}`, body);
        hapticNotify('success');
        nav(`/appointments/${id}`);
      } else {
        await api.post(`/appointments/${id}/reschedule`, {
          date,
          start_time,
          planned_duration_min,
        });
        hapticNotify('success');
        nav(`/appointments/${id}`);
      }
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
      qc.invalidateQueries({ queryKey: ['clients'] });
      if (id) qc.invalidateQueries({ queryKey: ['appointment', id] });
    } catch (e: any) {
      hapticNotify('error');
      const err = e?.response?.data?.error;
      if (e?.response?.status === 409 && (typeof err === 'string' ? err : '').includes('Конфликт')) {
        // Сервер строго запрещает пересечения; показываем понятное сообщение, без ложного «Сохранить всё равно».
        setError('Это время уже занято другой записью. Выберите другое время.');
      } else {
        setError(typeof err === 'string' ? err : 'Ошибка сохранения');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const title =
    mode === 'create' ? 'Новая запись' : mode === 'edit' ? 'Редактирование' : 'Перенос записи';

  // We deliberately don't use the Telegram MainButton here. On iOS it gets
  // pinned above the on-screen keyboard and overlaps inner sheets (e.g. the
  // "Новый клиент" form), which is confusing — users see two submit buttons
  // and can't tell which one is for which form. The page renders its own
  // sticky bottom button which is hidden whenever a sheet/dialog is open.

  return (
    <div className="animate-in-up space-y-4 pb-24">
      <PageHeader
        title={title}
        subtitle={mode === 'create' ? 'Заполните основные поля' : undefined}
      />

      {mode !== 'reschedule' && (
        <Card>
          <CardContent className="space-y-3 pt-4">
            <div>
              <Label>Клиент</Label>
              <ClientPicker
                value={client_id}
                onChange={setClientId}
                onCreated={(c) => setClientId(c.id)}
                onOpenChange={setPickerOpen}
              />
            </div>
            <div>
              <Label>Услуга</Label>
              <Select value={service_id} onValueChange={setServiceId}>
                <SelectTrigger><SelectValue placeholder="Выберите услугу" /></SelectTrigger>
                <SelectContent>
                  {services.map((s: any) => (
                    <SelectItem key={s.id} value={s.id}>
                      <span style={{ color: s.color }}>● </span>{s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="grid grid-cols-2 gap-3 pt-4">
          <div>
            <Label>Дата</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div>
            <Label>Начало</Label>
            <Input type="time" value={start_time} onChange={(e) => setStartTime(e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label>Длительность (мин)</Label>
            <Input
              type="number"
              inputMode="numeric"
              min={15}
              step={15}
              value={duration === '' ? '' : duration}
              onFocus={(e) => e.currentTarget.select()}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '') {
                  setDuration('');
                  return;
                }
                // Strip leading zeros so manual input like "030" becomes "30".
                const cleaned = v.replace(/^0+(?=\d)/, '');
                const n = Number(cleaned);
                if (!Number.isNaN(n)) setDuration(n);
              }}
              onBlur={() => {
                if (duration === '' || Number(duration) < 15) setDuration(15);
              }}
            />
            <div className="mt-2 flex flex-wrap gap-1">
              {[60, 120, 180, 240, 300, 360].map((m) => (
                <Button
                  key={m}
                  type="button"
                  size="sm"
                  variant={duration === m ? 'default' : 'outline'}
                  onClick={() => setDuration(m)}
                >
                  {m / 60}ч
                </Button>
              ))}
            </div>
          </div>
          {isOutOfHours && (
            <div className="col-span-2 rounded-xl bg-warning-soft p-3 text-xs font-medium text-warning">
              Время вне рабочих часов. Можно сохранить — это просто предупреждение.
            </div>
          )}
        </CardContent>
      </Card>

      {service?.needs_strands && mode !== 'reschedule' && (
        <Card>
          <CardContent className="space-y-3 pt-4">
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Прядей</Label>
                <Input
                  type="number"
                  inputMode="numeric"
                  value={strands}
                  onChange={(e) => setStrands(e.target.value === '' ? '' : Number(e.target.value))}
                />
              </div>
              <div>
                <Label>Длина (см)</Label>
                <Select
                  value={length_cm ? String(length_cm) : ''}
                  onValueChange={(v) => setLengthCm(Number(v))}
                >
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {Array.from(
                      new Map(lengths.map((l: any) => [l.length_cm, l])).values(),
                    )
                      .sort((a: any, b: any) => a.length_cm - b.length_cm)
                      .map((l: any) => (
                        <SelectItem key={l.id} value={String(l.length_cm)}>
                          {l.length_cm} см
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Грамм</Label>
                <Input
                  type="number"
                  step="0.1"
                  inputMode="decimal"
                  value={hair_grams}
                  onChange={(e) => setHairGrams(e.target.value === '' ? '' : Number(e.target.value))}
                />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-muted px-3 py-2">
              <Label className="!text-foreground">Свои волосы (мастер добавляет)</Label>
              <Switch checked={hair_added} onCheckedChange={setHairAdded} />
            </div>
            {hair_added && (
              <div>
                <Label>Стоимость волос (₽)</Label>
                <Input
                  type="number"
                  inputMode="numeric"
                  value={hair_price}
                  onChange={(e) => setHairPrice(e.target.value === '' ? '' : Number(e.target.value))}
                />
              </div>
            )}
            <div className="flex items-center justify-between rounded-xl bg-muted px-3 py-2">
              <Label className="!text-foreground">Распутывание колтунов</Label>
              <Switch checked={tangle_removal} onCheckedChange={setTangleRemoval} />
            </div>
            {tangle_removal && (
              <div>
                <Label>Стоимость распутывания (₽)</Label>
                <Input
                  type="number"
                  inputMode="numeric"
                  value={tangle_price}
                  onChange={(e) => setTanglePrice(e.target.value === '' ? '' : Number(e.target.value))}
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!service?.needs_strands && mode !== 'reschedule' && (
        <Card>
          <CardContent className="space-y-3 pt-4">
            <div className="flex items-center justify-between rounded-xl bg-muted px-3 py-2">
              <Label className="!text-foreground">Распутывание колтунов</Label>
              <Switch checked={tangle_removal} onCheckedChange={setTangleRemoval} />
            </div>
            {tangle_removal && (
              <div>
                <Label>Стоимость распутывания (₽)</Label>
                <Input
                  type="number"
                  inputMode="numeric"
                  value={tangle_price}
                  onChange={(e) => setTanglePrice(e.target.value === '' ? '' : Number(e.target.value))}
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {mode !== 'reschedule' && (
        <Card>
          <CardContent className="space-y-3 pt-4">
            <Label>Стоимость работы (₽)</Label>
            <Input
              type="number"
              inputMode="numeric"
              value={work_price}
              onChange={(e) => setWorkPrice(e.target.value === '' ? '' : Number(e.target.value))}
            />

            {!!extras.length && (
              <div>
                <Label>Доп. услуги</Label>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {extras.map((ex: any) => {
                    const picked = extras_picked.find((e) => e.extra_service_id === ex.id);
                    return (
                      <button
                        key={ex.id}
                        type="button"
                        onClick={() => toggleExtra(ex.id, Number(ex.price))}
                        className={
                          'rounded-full border px-3 py-1.5 text-xs transition-all ' +
                          (picked
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'hover:bg-accent')
                        }
                      >
                        {ex.name} · {fmtMoney(Number(ex.price))}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <Label>Заметка</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-primary/40 bg-primary/5">
        <CardContent className="flex items-center justify-between pt-4">
          <span className="text-sm text-muted-foreground">Итого</span>
          <span className="text-2xl font-bold">{fmtMoney(total)}</span>
        </CardContent>
      </Card>

      {error && <Card className="border-destructive bg-destructive/10"><CardContent className="pt-4 text-sm text-destructive">{error}</CardContent></Card>}

      {!pickerOpen && (
        <div className="fixed inset-x-0 bottom-0 z-30 mx-auto max-w-2xl border-t bg-background/90 p-3 backdrop-blur-xl safe-bottom">
          <Button onClick={submit} disabled={submitting} className="h-12 w-full text-base">
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {mode === 'create' ? 'Создать запись' : mode === 'edit' ? 'Сохранить' : 'Перенести'}
          </Button>
        </div>
      )}
    </div>
  );
};
